import { Col, Form, Input, Menu, Select } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AiOutlineCar,
  AiOutlineDelete,
  AiOutlineHistory,
  AiOutlinePrinter,
} from "react-icons/ai";
import { GiCancel } from "react-icons/gi";
import { FaTemperatureHigh } from "react-icons/fa";
import { GrMapLocation } from "react-icons/gr";
import { TbListDetails } from "react-icons/tb";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { cancelLabel, rtoCancelLabel, rtoLabel } from "../../common/common";
import { IBookingState } from "../../model/BookingModel";
import {
  ICommonState,
  IFilter,
  IPrintPieceBarCodes,
} from "../../model/CommonModel";
import { IOperation, IOperationState } from "../../model/OperationModel";
import { ITrackingHistoryState } from "../../model/TrackingHistoryModal";
import { SessionStorage } from "../../services/storage";
import { useBookingStore } from "../../stores/BookingStore";
import { useCommonStore } from "../../stores/CommonStore";
import { useOperationStore } from "../../stores/OperationStore";
import { useTrackingHistoryStore } from "../../stores/TrackingHistoryStore";
import {
  CANCEL_BOOKING_CATEGORY,
  CASH_ID,
  CountryEnum,
  PICKED,
  UserEnum,
  dropDownMapping,
  isAccessible,
  isRtoWaybill,
} from "../../utils/app-common";
import {
  numericValidation,
  RemarksLengthValidation,
  requiredValidation,
} from "../../validation/validation";
import ConfirmationModal from "../CommonComponent/ConfirmationModal";
import {
  TrackingHistoryComponent,
  TrackingHistoryDetailComponent,
} from "../TrackingHistoryComponent/TrackingHistoryComponent";

import LoadingModalComponent from "../CommonComponent/LoadingModalComponent";
import { RealtimetempratureComponent } from "../TZoneDeviceComponent/RealtimeTempratureComponent";
import TextArea from "antd/lib/input/TextArea";
import PickUpCancelPopup from "./PickupCancelRequestComponent";
import { useQueryClient } from "@tanstack/react-query";

type MenuListProps = {
  row: IOperation;
  setIsModalOpen: (isOpen: boolean) => void;
  pageSize: number;
  pageNumber: number;
  selectedRowKeys: React.Key[];
};
const optionList = dropDownMapping(
  [
    { id: "2/2", name: "2x2 sticker" },
    { id: "3/3", name: "3x3 sticker" },
  ],
  "id",
  "name"
);

export const PieceBarcodeComponent = ({
  row,
  isOpen,
  onClose,
}: {
  row: IOperation;
  isOpen: boolean;
  onClose: (value: boolean) => void;
}) => {
  const [form] = Form.useForm();

  const { promiseInProgress: operationPrintPromise } = usePromiseTracker({
    area: "operation-print-area",
  });
  const { promiseInProgress: downloadFileArea } = usePromiseTracker({
    area: "download-file-area",
  });

  const PrintPieceBarCodeAction = useCommonStore(
    (state: ICommonState) => state?.PrintPieceBarCodeAction
  );
  const onPieceConfirmedClicked = (values) => {
    const request: IPrintPieceBarCodes = {
      pieceCount: values?.pieceCount,
      waybillNo: row?.waybillNo,
      printType: values?.printType,
      originCity: row?.originCity,
      destinationCity: row?.destinationCity,
      productId: row?.productId,
    };
    trackPromise(PrintPieceBarCodeAction(request), "operation-print-area");
  };
  return (
    <ConfirmationModal
      okText="Print"
      title={`Print Piece Barcode - ${row?.waybillNo}`}
      promiseInProgress={operationPrintPromise}
      openModal={isOpen}
      confirmClicked={form.submit}
      hideModal={() => onClose(false)}
    >
      <LoadingModalComponent isLoading={downloadFileArea}>
        <Form form={form} onFinish={onPieceConfirmedClicked} autoComplete="off">
          <Col>
            <Form.Item
              name="pieceCount"
              label="Piece Count"
              labelCol={{ span: 24 }}
              rules={[{ ...numericValidation, ...requiredValidation }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              name="printType"
              rules={[requiredValidation]}
              label="Print Type"
              labelCol={{ span: 24 }}
            >
              <Select
                showSearch
                allowClear
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLocaleLowerCase()
                    ?.includes(input?.toLocaleLowerCase())
                }
              >
                {optionList?.map((opt) => (
                  <React.Fragment key={opt?.id}>
                    <Select.Option key={opt?.id} value={opt?.id}>
                      {opt?.value}
                    </Select.Option>
                  </React.Fragment>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Form>
      </LoadingModalComponent>
    </ConfirmationModal>
  );
};
const MenuList = ({
  row,
  setIsModalOpen,
  pageNumber,
  pageSize,
}: MenuListProps) => {
  const navigate: NavigateFunction = useNavigate();
  const queryClient = useQueryClient();
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [isTempratureTrackingModalOpen, setIsTempratureTrackingModalOpen] =
    useState(false);
  const [isConfirmationModal, setIsConfirmationModal] =
    useState<boolean>(false);
  const [isPickupCancelModal, setIsPickupCancelModal] =
    useState<boolean>(false);
  const [isConfirmationPieceModal, setIsConfirmationPieceModal] =
    useState<boolean>(false);
  const [isRTOConfirmationModal, setIsRTOConfirmationModal] =
    useState<boolean>(false);
  const [form] = Form.useForm();
  const { exceptionList } = JSON.parse(
    SessionStorage.get("app-storage") || "{}"
  );

  const cancelBookingExceptionListMemo = useMemo(() => dropDownMapping(exceptionList, 'exceptionId', 'exception'), [exceptionList]).filter(item => item?.exceptionCategoryId == CANCEL_BOOKING_CATEGORY);

  const { promiseInProgress: rtoProgress } = usePromiseTracker({
    area: "rto-area",
  });
  const { promiseInProgress: cancelProgress } = usePromiseTracker({
    area: "cancel-booking-area",
  });
  const CancelBookingByIdAction = useBookingStore(
    (state: IBookingState) => state?.CancelBookingByIdAction
  );
  const RTOByIdAction = useOperationStore(
    (state: IOperationState) => state?.RTOByIdAction
  );
  const ClearTrackingStore = useTrackingHistoryStore(
    (state: ITrackingHistoryState) => state?.ClearTrackingStore
  );
  const ClearTemperatureTrackingStore = useTrackingHistoryStore(
    (state: ITrackingHistoryState) => state?.ClearTemperatureTrackingStore
  );
  const TrackWayBillAction = useBookingStore(
    (state: IBookingState) => state?.TrackWayBillAction
  );
  const rtoResponse = useOperationStore((state) => state?.rtoResponse);

  const PrintCashRecieptAction = useCommonStore(
    (state: ICommonState) => state?.PrintCashRecieptAction
  );
  const PrintIRSReportAction = useCommonStore(
    (state: ICommonState) => state?.PrintIRSReportAction
  );

  const onPrintCashRecieptHandle = useCallback(() => {
    trackPromise(
      PrintCashRecieptAction(row?.waybillNo),
      "operation-print-area"
    );
  }, [PrintCashRecieptAction, row?.waybillNo]);

  const onPrintIRSReportHandler = () => {
    trackPromise(PrintIRSReportAction(row?.waybillNo), "operation-print-area");
  };

  const onPrintPieceBarcodeHandle = () => setIsConfirmationPieceModal(true);

  useEffect(() => {
    if (rtoResponse) {
      setIsRTOConfirmationModal(false);
      useOperationStore.setState({ rtoResponse: undefined });
    }
  }, [rtoResponse]);

  const onTrackingHistoryHandle = useCallback((row) => {
    ClearTrackingStore();
    useTrackingHistoryStore.setState({ rowDetail: row });
    navigate(`/operation/tracking-history/${row?.waybillNo}`);
  }, [ClearTrackingStore, navigate]);

  const onTempratureTrackingHandle = useCallback(() => {
    ClearTemperatureTrackingStore();
    setIsTempratureTrackingModalOpen(true);
  }, [ClearTemperatureTrackingStore]);

  const onCancelOperationHandle = useCallback(() => {
    setIsConfirmationModal(true);
  }, []);
  const onRTOConfirmationHandler = useCallback(async () => {
    await trackPromise(
      RTOByIdAction(row?.waybillNo, {
        count: pageSize,
        startIndex: pageNumber,
      }),
      "rto-area"
    );
    // 🔄 Trigger refetch of search filter query
    queryClient.invalidateQueries({ queryKey: ["operation-filter"], exact: false });
  }, [RTOByIdAction, pageNumber, pageSize, queryClient, row?.waybillNo]);

  const onRTOHandle = useCallback(() => {
    setIsRTOConfirmationModal(true);
  }, []);

  const isRto = isRtoWaybill(row?.waybillNo);

  const onConfirmedClicked = useCallback(async () => {
    if (cancelProgress) return false;
    // RTO cancel API does not take a reason
    if (!isRto && !form?.getFieldValue("cancelReasonId")) return false;
    await trackPromise(
      CancelBookingByIdAction(
        row?.id,
        { count: pageSize, startIndex: pageNumber },
        isRto ? undefined : {
          cancelReasonId: form?.getFieldValue("cancelReasonId"),
          cancelRemarks: form?.getFieldValue("cancelRemarks"),
        },
        undefined,
        row?.waybillNo
      ), "cancel-booking-area"
    );
    // 🔄 Trigger refetch of search filter query
    queryClient.invalidateQueries({ queryKey: ["operation-filter"], exact: false });
    setIsConfirmationModal(false);
  }, [form, CancelBookingByIdAction, row?.id, row?.waybillNo, isRto, cancelProgress, pageSize, pageNumber, queryClient]);

  const onTrackWayBill = useCallback(
    (waybillNo: string | undefined) => {
      trackPromise(TrackWayBillAction(waybillNo!), "tracking-way-bill");
    },
    [TrackWayBillAction]
  );
  const { pathname } = useLocation();

  const onDetailHandler = (record: IOperation) => {
    const {
      id,
      isPickedup,
      isPickupAck,
      isDelivered,
      lastStatus,
      productName,
      subProduct,
      temperature,
      shipperName,
      consigneeName,
      originCity,
      deliveryDate,
      assignVehicle,
      isIrs,
      originCountry,
      pickupDriverName,
      pickupDriverId,
      deliveryDriverName,
      waybillNo,
      bookingCode,
      billTypeId,
      billType,
      invoiced,
      isRTO,
      originalWaybill,
      bookingDate,
      pickupDate,
      isRescheduled,
      rtoWaybill,
      customerAccount,
      manifestConfirmed,
      isManualWaybill,
      subClientId,
      productId,
      subProductId,
      temperatureId,
      plateNumber,
      deliveryPlateNumber,
      deliveryDriverId,
      isDeliveryAck,
      pickupScanByEmpId,
      deliveryScanByEmpId,
      allowPickupAdHock,
      allowDeliveryAdHock,
      pieceCount,
      weight,
      adhocTripId,
      adhocVehicleId,
      adhocEmployee,
      adhocVehicle,
      adhocTripCode,
      pickupScanByEmpName,
      deliveryScanByEmpName,
      deliveryAdhocTripCode,
      industryGroup,
      isDatalogger
    } = record || {};
    const fromOperation = ["/operation"].includes(pathname);

    const selectedData = {
      subClientId,
      productId,
      subProductId,
      temperatureId,
      isRescheduled,
      deliveryDate,
      rtoWaybill,
      isPickupAck,
      isPickedup: Boolean(isPickedup),
      isDeliveryAck,
      isDelivered: Boolean(isDelivered),
      billTypeId,
      isRto: Boolean(isRTO),
      lastStatus,
      productName,
      subProduct,
      temperature,
      shipperName,
      consigneeName,
      originCity,
      originCountry,
      pickupDriverName,
      pickupDriverId,
      deliveryDriverName,
      waybillNo,
      bookingCode,
      id,
      billType,
      originalWaybillNo: originalWaybill,
      isInvoiced: invoiced,
      isIrs: Boolean(isIrs),
      bookingDate,
      pickupDate,
      customerAccount,
      assignVehicle,
      fromOperation,
      plateNumber,
      deliveryPlateNumber,
      deliveryDriverId,
      isManualWaybill: Boolean(isManualWaybill) && !isDelivered,
      manifestConfirmed: Boolean(manifestConfirmed),
      pickupScanByEmpId,
      deliveryScanByEmpId,
      allowPickupAdHock,
      allowDeliveryAdHock,
      pieceCount,
      weight,
      adhocTripId,
      adhocVehicleId,
      adhocEmployee,
      adhocVehicle,
      adhocTripCode,
      pickupScanByEmpName,
      deliveryScanByEmpName,
      deliveryAdhocTripCode,
      industryGroup,
      isDatalogger
    };

    SessionStorage.setObject("selectedOperation", selectedData);
    navigate(`/operation/${id}`, {
      state: selectedData,
    });
  };

  const hasNoAccess =
    isAccessible([
      UserEnum.SALES_DIRECTOR,
      UserEnum.MANIFESTER,
      UserEnum.OPERATION_CS_SUPERVISOR_MANIFESTER,
      UserEnum.DATA_ANALYST,
      UserEnum.OCC_SOLUTION,
      UserEnum.FINANCE,
      UserEnum.OPERATION_SUPERVISOR,
      UserEnum.CUSTOMER_SERVICE,
      UserEnum.SALES_MANAGER,
      UserEnum.FINANCE_CONTROLLER,
      UserEnum.DATA_LOGGER,
      UserEnum.OPERATION_SUPERVISOR_OPERATION_STAFF,
    ]) || row?.isDelivered;
  const isRTOAccessible = !hasNoAccess;
  const noAccessForMenuDetail = isAccessible([
    UserEnum.FINANCE,
    UserEnum.SALES_MANAGER,
    UserEnum.FINANCE_CONTROLLER,
    UserEnum.DATA_LOGGER,
  ]);
  const rtoMenuItem = {
    key: 4,
    label: "RTO",
    icon: <AiOutlineCar size={18} />,
    onClick: () => onRTOHandle(),
  };
  const isPickedUp = row?.isPickedup === "1";
  const trackTemperatureMenuItem = {
    key: 8,
    label: "Track Temperature",
    icon: <FaTemperatureHigh size={18} />,
    onClick: () => onTempratureTrackingHandle(),
  };

  const PickupCancelPermission = isAccessible([
    UserEnum.OPERATION_SUPERVISOR,
    UserEnum.OPERATION_SUPERVISOR_OPERATION_STAFF,
    UserEnum.OPS_MANAGER,
    UserEnum.OPERATION_SUPERVISOR_CASH_COLLECTOR,
    UserEnum.OPERATION_STAFF,
    UserEnum.OPS_MANAGER
  ]);

  return (
    <>
      <Menu
        onClick={() => setIsModalOpen(false)}
        items={[
          {
            key: 1,
            label: "Details",
            disabled: row?.isCancelled,
            icon: <TbListDetails size={18} />,
            onClick: () => onDetailHandler(row),
          },

          !hasNoAccess && !row?.isPickedup
            ? {
              // No = false
              key: 3,
              danger: true,
              label: isRto ? "RTO Cancel" : "Cancel",
              disabled: row?.isCancelled,
              icon: <AiOutlineDelete size={18} />,
              onClick: () => onCancelOperationHandle(),
            }
            : isRTOAccessible
              ? rtoMenuItem
              : null,
          // {
          //   key: 5,
          //   label: 'Print Waybill',
          //   title: 'Print Waybill',
          //   icon: <AiOutlinePrinter size={18} />,
          //   onClick: () => onPrintWayBillHandle(),
          // disabled: Boolean(selectedRowKeys.length)

          // }, {
          //   key: 6,
          //   label: 'Print Sticker',
          //   title: 'Print Sticker',
          //   icon: <AiOutlinePrinter size={18} />,
          //   onClick: () => onPrintStickerHandle(),
          // disabled: Boolean(selectedRowKeys.length)
          // },
          noAccessForMenuDetail || !Boolean(row?.isIrs)
            ? null
            : {
              key: 10,
              icon: <AiOutlinePrinter size={18} />,
              label: "Print IRS Report",
              onClick: () => onPrintIRSReportHandler(),
            },
          noAccessForMenuDetail
            ? null
            : {
              key: 9,
              icon: <AiOutlinePrinter size={18} />,
              label: "Print Piece Barcode",
              onClick: () => onPrintPieceBarcodeHandle(),
            },
          noAccessForMenuDetail ||
            row?.billTypeId !== CASH_ID ||
            !row?.isPickedup
            ? null
            : {
              key: 10,
              icon: <AiOutlinePrinter size={18} />,
              label: "Print Cash Receipt",
              onClick: () => onPrintCashRecieptHandle(),
            },
          noAccessForMenuDetail
            ? null
            : {
              key: 7,
              icon: <GrMapLocation size={18} />,
              label: "Track Vehicle",
              onClick: () => onTrackWayBill(row?.waybillNo),
            },
          noAccessForMenuDetail
            ? null
            : {
              key: 2,
              label: "Tracking History",
              icon: <AiOutlineHistory size={17} />,
              onClick: () => onTrackingHistoryHandle(row),
            },
          noAccessForMenuDetail
            ? null
            : isPickedUp
              ? trackTemperatureMenuItem
              : null,
          // pickup cancel request
          PickupCancelPermission && row?.lastStatus === PICKED
            ? {
              key: 10,
              danger: true,
              icon: <GiCancel size={18} />,
              label: "Pickup Cancel Request",
              onClick: () => { setIsPickupCancelModal(true) },
            }
            : null,
        ]}
      />
      {isConfirmationModal ? (
        <ConfirmationModal
          openModal={isConfirmationModal}
          confirmClicked={() => onConfirmedClicked()}
          hideModal={() => !cancelProgress && setIsConfirmationModal(false)}
          label={isRto ? rtoCancelLabel(row?.waybillNo) : cancelLabel(row?.bookingCode!)}
          promiseInProgress={cancelProgress}
          {...(isRto && { title: "Cancel RTO Waybill", okText: "Yes, Cancel", okType: "danger" as const })}
        >
          <Form form={form} hidden={isRto}>
            <Col>
              <Form.Item
                name="cancelReasonId"
                rules={[requiredValidation]}
                label="What is the main reason for the cancellation?"
                labelCol={{ span: 24 }}
              >
                <Select
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      ?.toLocaleLowerCase()
                      ?.includes(input?.toLocaleLowerCase())
                  }
                >
                  {cancelBookingExceptionListMemo?.map((account) => (
                    <React.Fragment key={account?.exceptionId}>
                      <Select.Option key={account?.exceptionId} value={account?.exceptionId}>
                        {account?.exception}
                      </Select.Option>
                    </React.Fragment>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name='cancelRemarks' label={'Remarks'} labelCol={{ span: 24 }}
                rules={[RemarksLengthValidation]}
              >
                <TextArea rows={4} cols={8} placeholder="Remarks" autoComplete={'off'} />
              </Form.Item>
            </Col>
          </Form>
        </ConfirmationModal>
      ) : null}
      <PickUpCancelPopup
        visible={isPickupCancelModal}
        onClose={() => setIsPickupCancelModal(false)}
        data={row} />
      {isRTOConfirmationModal ? (
        <ConfirmationModal
          promiseInProgress={rtoProgress}
          openModal={isRTOConfirmationModal}
          confirmClicked={() => onRTOConfirmationHandler()}
          hideModal={() => setIsRTOConfirmationModal(false)}
          label={rtoLabel(row?.bookingCode!)}
        />
      ) : null}

      {isTrackingModalOpen ? (
        <TrackingHistoryComponent
          isOpen={isTrackingModalOpen}
          closeModal={() => setIsTrackingModalOpen(false)}
          data={row?.waybillNo || row?.bookingCode}
        >
          <TrackingHistoryDetailComponent />
        </TrackingHistoryComponent>
      ) : null}

      {isTempratureTrackingModalOpen ? (
        <RealtimetempratureComponent
          isOpen={isTempratureTrackingModalOpen}
          closeModal={() => setIsTempratureTrackingModalOpen(false)}
          data={row?.waybillNo}
        />
      ) : null}
      {isConfirmationPieceModal ? (
        <PieceBarcodeComponent
          row={row}
          isOpen={isConfirmationPieceModal}
          onClose={(value) => setIsConfirmationPieceModal(value)}
        />
      ) : null}
    </>
  );
};

export default MenuList;
